import { Card } from '@stripe/stripe-js';
import { useCallback, useState } from 'react';

import { useFormContext } from 'components/Form/FormContext';
import { useFormSubmitInterceptor } from 'components/Form/FormSubmitInterceptor';

import { useStripeCreateCreditCardToken } from './useStripeCreateCreditCardToken';

/**
 * This function injects values `stripeTokenId`, `expMonth` and `expYear` into
 * the form once the token is created.
 *
 * If failed, it returns the error.
 */
export function useStripeTokenFormInterceptor() {
  const [stripeTokenError, setStripeTokenError] = useState<string>();
  const { getValue, updateValues } = useFormContext();
  const { createStripeCreditCardToken } = useStripeCreateCreditCardToken();

  const interceptor = useCallback(async () => {
    try {
      setStripeTokenError(undefined);

      const token = await createStripeCreditCardToken({
        additionalData: {
          name: getValue('name') as string,
          address_line1: getValue('addressLine1') as string,
          address_line2: getValue('addressLine2') as string,
          address_city: getValue('addressCity') as string,
          address_state: getValue('addressState') as string,
          address_zip: getValue('addressZip') as string,
          address_country: getValue('addressCountry') as string,
        },
      });

      const card = token.card as Card;
      updateValues({
        stripeTokenId: token.id,
        expMonth: card.exp_month,
        expYear: card.exp_year,
      });
    } catch (error) {
      setStripeTokenError(error.message);
    }
  }, [createStripeCreditCardToken, getValue, updateValues]);

  useFormSubmitInterceptor({ onIntercept: interceptor });

  return { stripeTokenError };
}
